<template>
  <div>
    <transition
      name="slideTransition"
      @after-enter="handleTransitionEnd"
    >
      <div
        v-if="open"
        v-on-clickaway="hideList"
        :class="{
          [$style.dropDownListContainer]: true,
          [$style.dropDownAlignRight]: dropDownAlignRight
        }"
      >
        <Listbox
          v-if="showListBox"
          :options="listBoxOptions"
          :showSelectAll="canSelectAll"
          :value="listBoxValue"
          @input="handleChange"
          @selectAll="handleSelectAll"
        >
          <template
            v-if="totalOptions"
            v-slot:footer
          >
            <ParagraphBody bodySize="tiny">
              <i>Showing {{ options.length }} of {{ totalOptions }}. Search to narrow results.</i>
            </ParagraphBody>
          </template>
        </Listbox>
        <div
          v-if="loading"
          :class="$style.loadingContainer"
        >
          <LoadingIcon :class="$style.loadingIcon" />
        </div>
        <div
          v-if="showErrorMessage"
          :class="$style.errorText"
          :data-test-id="$testId('errorText')"
        >
          <ParagraphBody bodySize="tiny">
            <i>{{ errorText }}</i>
          </ParagraphBody>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  Listbox,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import { mixin as clickaway } from 'vue-clickaway';
import LoadingIcon from 'public/img/icons/loading.svg?inline';

const { ListboxItem } = Types;

export default {
  name: 'MultiselectDropdown',

  components: {
    Listbox,
    LoadingIcon,
    ParagraphBody,
  },

  mixins: [clickaway],

  props: {
    canSelectAll: Boolean,
    errorMessage: {
      type: String,
      default: '',
    },
    loading: Boolean,
    open: Boolean,
    options: {
      type: Array,
      default: () => [],
    },
    totalOptions: {
      type: Number,
      default: undefined,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    dropDownAlignRight: false,
    maxResultsToShow: 500,
    openingList: false,
  }),

  computed: {
    errorText() {
      let message = '';

      if (this.errorMessage) {
        message = this.errorMessage;
      }

      if (this.noResultsFound) {
        message = 'No options available';
      }

      return message;
    },

    hasSelectedAllOptions() {
      return this.options.every((option) => this.value.includes(option));
    },

    listBoxOptions() {
      return this.options.map(({ name, value }) => new ListboxItem({ name, value }));
    },

    listBoxValue() {
      return this.hasSelectedAllOptions ? this.listBoxOptions : this.value;
    },

    noResultsFound() {
      return !this.options.length;
    },

    showErrorMessage() {
      return !this.loading && this.errorText;
    },

    showListBox() {
      return !this.loading && !this.errorText;
    },
  },

  methods: {
    getRequiredOptions() {
      return this.options.filter(({ required }) => required);
    },

    handleChange(updatedSelection) {
      this.$emit('input', updatedSelection);
    },

    handleSelectAll() {
      const newValue = this.hasSelectedAllOptions
        // required options stay checked
        ? this.getRequiredOptions()
        : this.options;

      this.handleChange(newValue);
    },

    handleTransitionEnd() {
      this.openingList = false;
    },

    hideList() {
      this.$emit('close');
    },
  },
};
</script>

<style module>
.dropDownAlignRight {
  right: 0;
}

.dropDownListContainer {
  background-color: var(--jcWhite50);
  border: 1px solid var(--jcGrey200);
  border-radius: 4px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.29563);
  display: flex;
  flex-direction: column;
  max-height: 320px;
  max-width: 350px;
  min-width: 250px;
  padding: 15px 22px;
  position: absolute;
  transition: var(--allTransition);
  z-index: 2;
}

.errorText {
  padding: 13px 17px 0 17px;
  text-align: center;
}

.loadingContainer {
  padding: 40px;
  text-align: center;
}

.loadingIcon {
  animation: spin 2s infinite linear;
  fill: var(--jcPrimary);
  width: 22px;
}

.slideTransition,
.slideTransition-enter-active,
.slideTransition-leave-active {
  transition: max-height 0.2s ease-in-out;
}

.slideTransition-enter,
.slideTransition-leave-to {
  max-height: 0;
  overflow: hidden;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

i {
  font-style: italic;
}

</style>
